import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import EmailForm from "./EmailForm";
import OtpForm from "./OtpForm";
import AuthXLogo from "./AuthXLogo";
import aapmorLogo from "../../assets/Aapmorlogo.svg";
import { loginEmail, verifyOtp } from "../../apiCalls/Apicalls";
import {
  clearAuthCookies,
  isTokenExpiringSoon,
  persistAuthSession,
  refreshAuthSession,
} from "../../utilities/authUtils";

const SsoLoadingScreen = ({ appName, hasRedirect }) => {
  const destination = appName ? appName.toUpperCase() : "your app";

  return (
    <div className="ax-sso">
      <main className="ax-sso-stage ax-sso-loading-stage" aria-live="polite">
        <div className="ax-sso-brand">
          <AuthXLogo variant="dark" size="lg" />
          <span className="ax-sso-tag">Single Sign-On</span>
        </div>

        <section className="ax-sso-loading-card" role="status">
          <span className="ax-sso-loader" aria-hidden="true" />
          <div className="ax-sso-loading-copy">
            <h1>{hasRedirect ? `Connecting to ${destination}` : "Checking your session"}</h1>
            <p>
              {hasRedirect
                ? "AuthX is completing your secure sign-in. You will be redirected automatically."
                : "AuthX is verifying your saved sign-in before continuing."}
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

function Login() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [getOtp, setGetOtp] = useState(false);
  const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [submittingOTP, setSubmittingOTP] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();
  const [queryParameters] = useSearchParams();
  const [searchParams] = useSearchParams();
  const appName = searchParams.get("app");
  const hasRedirect = Boolean(queryParameters.get("redirect_uri"));

  const buildRedirectUrl = (redirectUri, authToken) => {
    if (!redirectUri) {
      return null;
    }

    const decodedRedirectUri = decodeURIComponent(redirectUri);

    try {
      const callbackUrl = new URL("/login", decodedRedirectUri);
      callbackUrl.searchParams.set("token", authToken);
      return callbackUrl.toString();
    } catch (error) {
      return `${decodedRedirectUri.replace(/\/+$/, "")}/login?token=${encodeURIComponent(authToken)}`;
    }
  };

  useEffect(() => {
    const redirectUri = queryParameters.get("redirect_uri");
    const redirectDomain = queryParameters.get("domain");
    const existingToken = Cookies.get("jwtToken");

    const continueWithSession = async () => {
      if (redirectUri) {
        if (existingToken && !isTokenExpiringSoon(existingToken, 60)) {
          const redirectUrl = buildRedirectUrl(redirectUri, existingToken);
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          }
        }

        try {
          const refreshed = await refreshAuthSession({ redirectDomain });
          if (refreshed?.jwt_token) {
            const redirectUrl = buildRedirectUrl(redirectUri, refreshed.jwt_token);
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
          }
        } catch (error) {
          clearAuthCookies(redirectDomain);
        }

        setCheckingSession(false);
        return;
      }

      if (existingToken && !isTokenExpiringSoon(existingToken, 300)) {
        navigate("/dashboard/home");
        return;
      }

      try {
        const refreshed = await refreshAuthSession({ redirectDomain });
        if (refreshed?.jwt_token && window.location.pathname === "/") {
          navigate("/dashboard/home");
          return;
        }
      } catch (error) {
        clearAuthCookies(redirectDomain);
      }

      setCheckingSession(false);
    };

    continueWithSession();
  }, [navigate, queryParameters]);

  const validateEmail = (value) => {
    const errors = {};
    const trimmedEmail = value.trim();
    const regex = /^[a-zA-Z._%+-][a-zA-Z0-9._%+-]*@[a-zA-Z]+\.[a-zA-Z]{2,}$/;

    if (!trimmedEmail) {
      errors.email = "Field is required";
    } else if (!regex.test(trimmedEmail)) {
      errors.email = "Invalid email format";
    } else {
      const domainParts = trimmedEmail.split("@")[1].split(".");
      if (domainParts.length > 3) {
        errors.email = "Invalid email format";
      }
    }

    return { errors, trimmedEmail };
  };

  const validateOtp = (value) => {
    const errors = {};
    if (!value) {
      errors.otp = "OTP is required";
    } else if (!/^\d+$/.test(value)) {
      errors.otp = "OTP must contain only digits";
    } else if (value.length !== 6) {
      errors.otp = "OTP must be 6 digits long";
    }
    return errors;
  };

  const handleGetOTP = async () => {
    const { errors, trimmedEmail } = validateEmail(email);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(loginEmail, { email: trimmedEmail });
      setGetOtp(true);
      setCanResend(false);
      setTimer(30);
    } catch (error) {
      toast.error("Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpArray(new Array(6).fill(""));
    setLoading(true);
    setCanResend(false);
    setTimer(30);
    try {
      await axios.post(loginEmail, { email });
      toast.success("OTP resent successfully!");
    } catch (error) {
      toast.error("Error resending OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOTP = async () => {
    if (submittingOTP) {
      return;
    }

    const errors = validateOtp(otp);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setSubmittingOTP(true);
    try {
      const response = await axios.post(
        verifyOtp,
        { otp, email },
        { withCredentials: true },
      );

      if (!response.data.jwt_token) {
        toast.error("Invalid OTP.");
        return;
      }

      const useremail = response.data.userEmail;
      const jwtToken = response.data.jwt_token;
      const redirectUri = queryParameters.get("redirect_uri");
      const redirectDomain = queryParameters.get("domain");
      const access = response.data.access;
      persistAuthSession({
        jwtToken,
        userEmail: useremail,
        access,
        refreshToken: response.data.refresh_token,
        redirectDomain,
      });

      if (redirectUri) {
        const redirectUrl = buildRedirectUrl(redirectUri, jwtToken);
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } else {
        navigate("/dashboard/home");
        toast.success("Login successful!");
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied for this email address.");
      } else if (error.response?.status === 410) {
        toast.error(error.response.data.error);
      } else if (error.response?.status === 401) {
        toast.error(error.response.data.error);
      } else {
        toast.error("An error occurred while verifying OTP. Please try again.");
      }
    } finally {
      setSubmittingOTP(false);
    }
  };

  useEffect(() => {
    if (email.length > 0) {
      setFormErrors(validateEmail(email).errors);
    }
  }, [email]);

  useEffect(() => {
    if (!getOtp || canResend) {
      return undefined;
    }

    const interval = setInterval(() => {
      setTimer((previous) => {
        if (previous <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return previous - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [getOtp, canResend]);

  return (
    <>
      {checkingSession ? (
        <SsoLoadingScreen appName={appName} hasRedirect={hasRedirect} />
      ) : (
        <div className="ax-sso">
          <main className="ax-sso-stage">
            <div className="ax-sso-brand">
              <AuthXLogo variant="dark" size="lg" />
              <span className="ax-sso-tag">Single Sign-On</span>
            </div>

            <div className="ax-sso-card">
              {!getOtp ? (
                <EmailForm
                  email={email}
                  setEmail={setEmail}
                  formErrors={formErrors}
                  handleGetOTP={handleGetOTP}
                  loading={loading}
                  appName={appName}
                />
              ) : (
                <OtpForm
                  email={email}
                  otpArray={otpArray}
                  setOtpArray={setOtpArray}
                  otp={otp}
                  setOtp={setOtp}
                  formErrors={formErrors}
                  handleSubmitOTP={handleSubmitOTP}
                  submittingOTP={submittingOTP}
                  canResend={canResend}
                  timer={timer}
                  handleResendOTP={handleResendOTP}
                  setGetOtp={setGetOtp}
                  setCanResend={setCanResend}
                  appName={appName}
                />
              )}

              <footer className="ax-sso-card-footer" aria-label="Powered by AAPMOR">
                <span>Powered by</span>
                <img
                  src={aapmorLogo}
                  alt="AAPMOR"
                  className="ax-sso-aapmor-mark"
                />
              </footer>
            </div>

            <p className="ax-sso-terms">
              By continuing you agree to AuthX&rsquo;s{" "}
              <a href="#terms">Terms</a> and{" "}
              <a href="#privacy">Privacy Policy</a>.
            </p>
          </main>
        </div>
      )}
      <ToastContainer />
    </>
  );
}

export default Login;
